import { LovInfoDictionary } from "../../misc/LovInfoDictionary";
import { MonitoringData } from "./MonitoringData";
import * as columns from '../../misc/Columns.ts';
import * as constants from "../../misc/Constants.tsx";
import { LovInfo } from "../../misc/LovInfo";
import * as lovs from "../../misc/Lovs.ts";
import * as frac_api from "../../api/FracApi.ts";

type ResistanceData = frac_api.ResistanceData;
type Company = frac_api.Company;
type Country = frac_api.Country;
type ResistanceFrequency = frac_api.ResistanceFrequency;
type FracApi = frac_api.FracApi;
type ColumnInfo = columns.ColumnInfo;

export const convertFormDataToRecord = (data: MonitoringData, lovs: LovInfoDictionary): ResistanceData => {
    return {
        id: data.id,
        countries: data.country,
        year: String(data.year),
        frac_moa_code: data.frac_moa_code,
        pest_sci_name: data.pest_sci_name,
        crop_sci_name: data.crop_sci_name,
        companies: convertToArray(data.companies),
        monitoring_type: data.monitoring_type,
        monitoring_intensity: data.monitoring_intensity,
        resistance_frequency: convertToArray(data.resistance_frequency),
        resistance_mechanism: data.resistance_mechanism,
        mechanism_detail: data.mechanism_detail,
        geo_distribution: data.geo_distribution,
        resistance_factor: data.resistance_factor,
        resistance_field_relevance: data.resistance_field_relevance,
        comments: data.comments,
    };
};

export const convertToArray = (data: string | string[] | undefined): string[] => {
    if (typeof data === 'string') {
        return data.split(",").map(item => item.trim());
    }
    if (Array.isArray(data)) {
        return data.map(item => typeof item === 'string' ? item.trim() : item);
    }
    return [];
};

interface ValidationResult {
    valid: boolean;
    error: string;
}

export const validateRecord = (res: ResistanceData): ValidationResult => {
    if (!res.countries || res.countries.length === 0) {
        return { valid: false, error: "Country is required" };
    }
    if (!res.year) {
        return { valid: false, error: "Year is required" };
    }
    if (!res.pest_sci_name) {
        return { valid: false, error: "Pest scientific name is required" };
    }
    if (!res.crop_sci_name) {
        return { valid: false, error: "Crop scientific name is required" };
    }
    if (!res.companies || res.companies.length === 0) {
        return { valid: false, error: "Company is required" };
    }
    if (!res.monitoring_type) {
        return { valid: false, error: "Monitoring type is required" };
    }
    if (!res.monitoring_intensity) {
        return { valid: false, error: "Monitoring intensity is required" };
    }
    if (!res.resistance_frequency || res.resistance_frequency.length === 0) {
        return { valid: false, error: "Resistance frequency is required" };
    }
    if (!res.resistance_mechanism) {
        return { valid: false, error: "Resistance mechanism is required" };
    }
    if (!res.geo_distribution) {
        return { valid: false, error: "Geographic distribution is required" };
    }
    if (!res.resistance_factor) {
        return { valid: false, error: "Resistance factor is required" };
    }
    if (!res.resistance_field_relevance) {
        return { valid: false, error: "Relevance of resistance in the field is required" };
    }
    return { valid: true, error: "" };
};


export function experimentAllColumns(
    is_admin: boolean,
    setShowEdit: (showEdit: boolean) => void,
    setEditData: (editData: MonitoringData) => void,
    deleteRecord: (id: number) => void,
    companiesData: Company[],
    countriesData: Country[],
    freqsData: ResistanceFrequency[],
    api: FracApi
): ColumnInfo[] {

    const cols: ColumnInfo[] = [
        {
            rows: 1, wrap: true, type: 'str', max_disp_chars: 20, hidden: false, disp_name: 'Id', editable: false, id: 'id',
            sortFunction: (a: MonitoringData, b: MonitoringData) => (Number(a['id']) < Number(b['id']) ? -1 : 1)
        },
        { type: 'lov', lov: 'countries', multi: false, disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Country', editable: true, id: 'country' },
        { type: 'lov', lov: 'years', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Year', editable: true, id: 'year' },
        { type: 'lov', lov: 'moaCodes', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'FRAC MoA & code', editable: true, id: 'frac_moa_code' },
        { type: 'dynamic_lov', lov: 'pests_dynamic', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Pest scientific name', editable: true, id: 'pest_sci_name' },
        { type: 'dynamic_lov', lov: 'crops_dynamic', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Crop scientific name', editable: true, id: 'crop_sci_name' },
        { type: 'lov', lov: 'companies', multi: true, disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Contributing Companies', editable: true, id: 'companies' },
        { type: 'lov', lov: 'monitoringType', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Monitoring Type', editable: true, id: 'monitoring_type' },
        { type: 'lov', lov: 'monitoringIntensity', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Monitoring Intensity', editable: true, id: 'monitoring_intensity' },
        { type: 'lov', lov: 'resistanceFrequency', multi: true, disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Frequency of resistance', editable: true, id: 'resistance_frequency' },
        { type: 'lov', lov: 'resistanceMechanism', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Mechanism of resistance', editable: true, id: 'resistance_mechanism' },
        { disp_width: '50px', rows: 1, wrap: true, type: 'str', max_disp_chars: 300, hidden: false, disp_name: 'Details of the mechanism of resistance', editable: true, id: 'mechanism_detail' },
        { type: 'lov', lov: 'geoDistribution', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Geographic distribution', editable: true, id: 'geo_distribution' },
        { type: 'lov', lov: 'resistanceFactor', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Resistance factor', editable: true, id: 'resistance_factor' },
        { type: 'lov', lov: 'resistanceRelevance', disp_width: '50px', rows: 1, wrap: true, max_disp_chars: 300, hidden: false, disp_name: 'Relevance of resistance in the field', editable: true, id: 'resistance_field_relevance' },
        { disp_width: '50px', rows: 1, wrap: true, type: 'str', max_disp_chars: 300, hidden: false, disp_name: 'Comments', editable: true, id: 'comments' },
        { disp_width: '50px', rows: 1, wrap: true, type: 'date', max_disp_chars: 300, hidden: false, disp_name: 'Last update', editable: false, id: 'last_update' },
    ];

    if (is_admin) {
        cols.push(
            {
                rows: 1, wrap: true, type: 'action', max_disp_width: 20, hidden: false, disp_name: 'Edit', editable: false,
                id: constants.editActionId, action: 'Edit',
                perform_action: async (x: MonitoringData) => {
                    setShowEdit(true);
                    const convertedData = await convertLovsInRow(x, companiesData, countriesData, freqsData, api);
                    setEditData(convertedData);
                }
            },
            {
                rows: 1, wrap: true, type: 'action', max_disp_width: 20, hidden: false, disp_name: 'Delete', editable: false,
                id: constants.deleteActionId,
                action: 'Delete',
                perform_action: async (x: MonitoringData) => {
                    deleteRecord(x.id);
                }
            }
        );
    }

    return cols;
}


async function convertLovsInRow(
    monitorData: MonitoringData,
    companiesData: Company[],
    countriesData: Country[],
    freqsData: ResistanceFrequency[],
    api: FracApi
): Promise<MonitoringData> {

    const newMonitorData: MonitoringData = { ...monitorData };

    if (monitorData.crop_sci_name) {
        const crop = await api.getCropWithSciName(monitorData.crop_sci_name);
        newMonitorData.crop_sci_name = crop?.scientific_name || monitorData.crop_sci_name;
    }
    if (monitorData.pest_sci_name) {
        const pest = await api.getPestWithSciName(monitorData.pest_sci_name);
        newMonitorData.pest_sci_name = pest?.scientific_name || monitorData.pest_sci_name;
    }


    if (typeof newMonitorData.companies === 'string') {
        const companies = convertToArray(newMonitorData.companies);
        const companyLabels: string[] = companies.map(company => {
            const companyData = companiesData.find(comp => comp.name_code === company);
            return companyData?.name_code || company;
        });
        newMonitorData.companies = companyLabels;
    }

    if (typeof newMonitorData.resistance_frequency === 'string') {
        const frequencies = convertToArray(newMonitorData.resistance_frequency);
        const frequencyLabels: string[] = frequencies.map(frequency => {
            const freqData = freqsData.find(freq => freq.frequency === frequency);
            return freqData?.frequency || frequency;
        });
        newMonitorData.resistance_frequency = frequencyLabels;
    }

    return newMonitorData;
}